// HTTP API client. The session lives in an HttpOnly cookie, so requests
// only need credentials: 'same-origin'. The server checks the Origin header
// of state-changing requests; browsers add it automatically.
import type {
  AdminBoardRules,
  AdminHealth,
  AdminUser,
  ApiErrorBody,
  BoardSettings,
  BoardSettingsUpdate,
  BoardSlot,
  BoardSlotCreate,
  BoardSlotItems,
  BoardSlotUpdate,
  Bookmark,
  CalendarFeed,
  Bootstrap,
  Card,
  CardCreate,
  CardUpdate,
  DriveEntry,
  DriveListing,
  DriveSpace,
  FamilyEventCreate,
  FileObject,
  FrequentItem,
  HelperInfo,
  IceServers,
  Id,
  Invite,
  InviteCheck,
  InviteListItem,
  LedgerEntry,
  LedgerEntryInput,
  LedgerMonth,
  LinkPrice,
  LiveLocation,
  LocReport,
  LocReportAnswer,
  Me,
  Message,
  MessageReaction,
  MessagesPage,
  MonthSpending,
  Passkey,
  PasskeyAssertion,
  PasskeyCreateOptions,
  PasskeyLoginOptions,
  Place,
  PlaceInput,
  PriceTraces,
  PurchaseItemInput,
  QuietHours,
  ReactionKind,
  Room,
  RoomBackground,
  RoomBackgroundData,
  RoomFileItem,
  RoomLinksPage,
  ScheduledMessage,
  StickerSet,
  SearchPage,
  Session,
  SyncResponse,
  RoomTensorLink,
  TensorCardValue,
  TensorLinkSent,
  TensorLinkWorkbook,
  TrailInterval,
  TrailSettings,
  Trails,
  User,
} from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly retryAfterMs: number | undefined
  /** What a conflict names: the slot already there (duplicate), who claimed it (claimed). */
  readonly slotId: number | undefined
  readonly userId: number | undefined

  constructor(
    status: number,
    code: string,
    message: string,
    retryAfterMs?: number,
    names: { slot_id?: number; user_id?: number } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfterMs = retryAfterMs
    this.slotId = names.slot_id
    this.userId = names.user_id
  }
}

/** A problem found here, worded for people ("…보다 큰 파일은 올릴 수 없습니다."). */
export class UserError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserError'
  }
}

/** Network failure (offline, server down): the request may be retried. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

/** One JSON request (also used by the tensor app, src/tensor/api.ts). */
export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      cache: 'no-store',
    })
  } catch (e) {
    throw new NetworkError(e instanceof Error ? e.message : String(e))
  }
  if (res.status === 204) return undefined as T
  let data: unknown = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error
    if (res.status >= 502 && res.status <= 504 && !err) {
      throw new NetworkError(`HTTP ${res.status}`)
    }
    throw new ApiError(res.status, err?.code ?? 'internal', err?.message ?? `HTTP ${res.status}`, err?.retry_after_ms, {
      ...(err?.slot_id ? { slot_id: err.slot_id } : {}),
      ...(err?.user_id ? { user_id: err.user_id } : {}),
    })
  }
  return data as T
}

const get = <T>(path: string) => request<T>('GET', path)
const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, body)
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body)
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body)
const del = <T>(path: string) => request<T>('DELETE', path)

export const api = {
  login: (login: string, password: string, device_name: string) =>
    post<{ user: Me }>('/api/auth/login', { login, password, device_name }),
  logout: () => post<void>('/api/auth/logout'),
  passkeyLoginOptions: () => post<PasskeyLoginOptions>('/api/auth/passkey/options'),
  passkeyLogin: (body: PasskeyAssertion) => post<{ user: Me }>('/api/auth/passkey', body),
  inviteCheck: (token: string) => post<InviteCheck>('/api/auth/invite/check', { token }),
  inviteAccept: (body: { token: string; login?: string; name?: string; password: string; device_name: string }) =>
    post<{ user: Me }>('/api/auth/invite/accept', body),

  bootstrap: () => get<Bootstrap>('/api/bootstrap'),
  sync: (since: number, limit = 500) => get<SyncResponse>(`/api/sync?since=${since}&limit=${limit}`),
  users: () => get<{ users: User[] }>('/api/users'),
  /** photo_file_id: a picture I uploaded, or null for none (design 01a 9). */
  updateMe: (body: { name?: string; status?: string; photo_file_id?: string | null }) =>
    patch<{ user: Me }>('/api/me', body),
  changePassword: (current_password: string, new_password: string) =>
    post<void>('/api/me/password', { current_password, new_password }),
  sessions: () => get<{ sessions: Session[] }>('/api/me/sessions'),
  scheduled: () => get<{ scheduled: ScheduledMessage[] }>('/api/scheduled'),
  stickers: () => get<{ sets: StickerSet[] }>('/api/stickers'),
  bookmarks: () => get<{ bookmarks: Bookmark[] }>('/api/bookmarks'),
  bookmark: (roomId: Id, seq: number, on: boolean) =>
    request<void>(on ? 'PUT' : 'DELETE', `/api/rooms/${roomId}/messages/${seq}/bookmark`),
  seqAt: (roomId: Id, time: number) => get<{ seq?: number }>(`/api/rooms/${roomId}/seq-at?time=${time}`),
  setBackground: (roomId: Id, background: RoomBackground, file?: string) =>
    put<RoomBackgroundData>(`/api/rooms/${roomId}/background`, file ? { background, file } : { background }),
  createStickerSet: (name: string) => post<{ id: Id }>('/api/sticker-sets', { name }),
  renameStickerSet: (id: Id, name: string) => patch<void>(`/api/sticker-sets/${id}`, { name }),
  deleteStickerSet: (id: Id) => del<void>(`/api/sticker-sets/${id}`),
  addSticker: (setId: Id, file: string) => post<{ id: Id }>(`/api/sticker-sets/${setId}/stickers`, { file }),
  deleteSticker: (id: Id) => del<void>(`/api/stickers/${id}`),
  schedule: (roomId: Id, body: { client_msg_id: string; text: string; send_at: number }) =>
    post<{ id: Id }>(`/api/rooms/${roomId}/scheduled`, body),
  updateScheduled: (id: Id, body: { text?: string; send_at?: number }) => patch<void>(`/api/scheduled/${id}`, body),
  cancelScheduled: (id: Id) => del<void>(`/api/scheduled/${id}`),
  updateCurrentSession: (body: { device_name?: string; hide_preview?: boolean; quiet?: QuietHours | null }) =>
    patch<void>('/api/me/sessions/current', body),
  revokeSession: (id: Id) => del<void>(`/api/me/sessions/${id}`),
  callIce: () => get<IceServers>('/api/calls/ice'),
  keywords: () => get<{ keywords: string[] }>('/api/me/keywords'),
  setKeywords: (keywords: string[]) => put<{ keywords: string[] }>('/api/me/keywords', { keywords }),
  calendarFeed: () => get<{ feed: CalendarFeed | null }>('/api/me/calendar-feed'),
  renewCalendarFeed: () => post<{ feed: CalendarFeed; path: string }>('/api/me/calendar-feed'),
  deleteCalendarFeed: () => del<void>('/api/me/calendar-feed'),
  passkeys: () => get<{ passkeys: Passkey[] }>('/api/me/passkeys'),
  passkeyOptions: () => post<PasskeyCreateOptions>('/api/me/passkeys/options'),
  addPasskey: (body: { client_data: string; attestation: string; name?: string }) =>
    post<{ id: Id }>('/api/me/passkeys', body),
  removePasskey: (id: Id) => del<void>(`/api/me/passkeys/${id}`),
  pushSubscribe: (body: { endpoint: string; p256dh: string; auth: string }) => put<void>('/api/me/push', body),
  pushUnsubscribe: () => del<void>('/api/me/push'),
  pushTest: () => post<void>('/api/me/push/test'),

  rooms: () => get<{ rooms: Room[] }>('/api/rooms'),
  room: (id: Id) => get<{ room: Room }>(`/api/rooms/${id}`),
  createDm: (user_id: Id) => post<{ room: Room }>('/api/rooms', { kind: 'dm', user_id }),
  createGroup: (name: string, user_ids: Id[]) => post<{ room: Room }>('/api/rooms', { kind: 'group', name, user_ids }),
  renameRoom: (id: Id, name: string) => patch<{ room: Room }>(`/api/rooms/${id}`, { name }),
  /** A group room's photo (design 01a 2): a picture I uploaded, or null. */
  setRoomPhoto: (id: Id, photo_file_id: string | null) => patch<{ room: Room }>(`/api/rooms/${id}`, { photo_file_id }),
  addMembers: (id: Id, user_ids: Id[]) => post<{ room: Room }>(`/api/rooms/${id}/members`, { user_ids }),
  removeMember: (id: Id, userId: Id) => del<void>(`/api/rooms/${id}/members/${userId}`),
  messages: (id: Id, opts: { before?: number; after?: number; limit?: number }) => {
    const q = new URLSearchParams()
    if (opts.before) q.set('before', String(opts.before))
    if (opts.after) q.set('after', String(opts.after))
    if (opts.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return get<MessagesPage>(`/api/rooms/${id}/messages${qs ? `?${qs}` : ''}`)
  },
  /** Messages with q (design 01a 8): every room of mine, or one. */
  search: (q: string, opts: { roomId?: Id; before?: Id; limit?: number } = {}) => {
    const p = new URLSearchParams({ q })
    if (opts.roomId) p.set('room_id', String(opts.roomId))
    if (opts.before) p.set('before', String(opts.before))
    if (opts.limit) p.set('limit', String(opts.limit))
    return get<SearchPage>(`/api/search?${p}`)
  },
  send: (id: Id, body: { client_msg_id: string; text: string; reply_to?: Id; board_slot?: Id }) =>
    post<{ message: Message }>(`/api/rooms/${id}/messages`, { kind: 'text', ...body }),
  sendFiles: (id: Id, body: { client_msg_id: string; files: string[]; reply_to?: Id }) =>
    post<{ message: Message }>(`/api/rooms/${id}/messages`, { kind: 'file', ...body }),
  sendVoice: (id: Id, body: { client_msg_id: string; files: string[]; waveform: string; reply_to?: Id }) =>
    post<{ message: Message }>(`/api/rooms/${id}/messages`, { kind: 'voice', ...body }),
  sendSticker: (id: Id, body: { client_msg_id: string; sticker_id: Id; reply_to?: Id }) =>
    post<{ message: Message }>(`/api/rooms/${id}/messages`, { kind: 'sticker', ...body }),
  roomFiles: (id: Id, opts: { kind?: 'media' | 'other'; before?: number; limit?: number }) => {
    const q = new URLSearchParams()
    if (opts.kind) q.set('kind', opts.kind)
    if (opts.before) q.set('before', String(opts.before))
    if (opts.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return get<{ items: RoomFileItem[]; has_more: boolean }>(`/api/rooms/${id}/files${qs ? `?${qs}` : ''}`)
  },
  /** The addresses written in a room, newest first (design 01a 3, the drawer). */
  roomLinks: (id: Id, opts: { before?: number; limit?: number }) => {
    const q = new URLSearchParams()
    if (opts.before) q.set('before', String(opts.before))
    if (opts.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return get<RoomLinksPage>(`/api/rooms/${id}/links${qs ? `?${qs}` : ''}`)
  },
  file: (fileId: string) => get<{ file: FileObject }>(`/api/files/${encodeURIComponent(fileId)}`),

  driveList: (space: DriveSpace, opts: { folder?: number; sort?: 'name' | 'new'; offset?: number; limit?: number }) => {
    const q = new URLSearchParams({ space })
    if (opts.folder) q.set('folder', String(opts.folder))
    if (opts.sort) q.set('sort', opts.sort)
    if (opts.offset) q.set('offset', String(opts.offset))
    if (opts.limit) q.set('limit', String(opts.limit))
    return get<DriveListing>(`/api/drive?${q.toString()}`)
  },
  driveTrashList: (space: DriveSpace) => get<{ entries: DriveEntry[] }>(`/api/drive/trash?space=${space}`),
  driveCreateFolder: (space: DriveSpace, parent: number, name: string) =>
    post<{ entry: DriveEntry }>('/api/drive/folders', { space, name, ...(parent ? { parent_id: parent } : {}) }),
  driveAddFiles: (space: DriveSpace, parent: number, files: string[]) =>
    post<{ entries: DriveEntry[] }>('/api/drive/files', { space, files, ...(parent ? { parent_id: parent } : {}) }),
  driveRename: (id: Id, name: string) => patch<{ entry: DriveEntry }>(`/api/drive/entries/${id}`, { name }),
  driveMove: (space: DriveSpace, ids: Id[], parent: number) =>
    post<{ entries: DriveEntry[] }>('/api/drive/move', { space, ids, ...(parent ? { parent_id: parent } : {}) }),
  driveTrash: (ids: Id[]) => post<void>('/api/drive/trash', { ids }),
  driveRestore: (ids: Id[]) => post<{ entries: DriveEntry[] }>('/api/drive/restore', { ids }),
  drivePurge: (space: DriveSpace, ids?: Id[]) => post<void>('/api/drive/purge', { space, ...(ids ? { ids } : {}) }),
  deleteMessage: (id: Id, seq: number) => del<void>(`/api/rooms/${id}/messages/${seq}`),
  editMessage: (id: Id, seq: number, text: string) =>
    patch<{ message: Message }>(`/api/rooms/${id}/messages/${seq}`, { text }),
  react: (id: Id, seq: number, reaction: ReactionKind) =>
    put<{ reactions: MessageReaction[] }>(`/api/rooms/${id}/messages/${seq}/reaction`, { reaction }),
  unreact: (id: Id, seq: number) => del<{ reactions: MessageReaction[] }>(`/api/rooms/${id}/messages/${seq}/reaction`),
  hideMessage: (id: Id, seq: number) => post<void>(`/api/rooms/${id}/messages/${seq}/hide`, {}),
  forwardMessage: (to: Id, clientMsgId: string, fromRoom: Id, fromSeq: number) =>
    post<{ message: Message }>(`/api/rooms/${to}/messages`, {
      client_msg_id: clientMsgId,
      kind: 'forward',
      from_room_id: fromRoom,
      from_seq: fromSeq,
    }),

  createCard: (roomId: Id, body: CardCreate) => post<{ message: Message }>(`/api/rooms/${roomId}/cards`, body),
  roomCards: (roomId: Id, type: 'event' | 'purchase' | 'poll' | 'location') =>
    get<{ cards: Card[] }>(`/api/rooms/${roomId}/cards?type=${type}`),
  /** The calendar's events and bought carts in [from, to) (design 01c 3.7); room: that room's only. */
  calendar: (from: number, to: number, room?: Id) =>
    get<{ events: Card[]; carts: Card[] }>(`/api/calendar?from=${from}&to=${to}${room !== undefined ? `&room=${room}` : ''}`),
  /** Carts of all my rooms, or of one (design 01c 3.9); deleted: the ones that can be brought back (3.6). */
  carts: (q: { room?: Id; deleted?: boolean } = {}) => {
    const p = new URLSearchParams()
    if (q.room !== undefined) p.set('room', String(q.room))
    if (q.deleted) p.set('deleted', 'true')
    const qs = p.toString()
    return get<{ carts: Card[] }>(`/api/carts${qs ? `?${qs}` : ''}`)
  },
  /** Brings a deleted cart back (design 01c 3.6). */
  restoreCard: (id: Id) => post<{ card: Card }>(`/api/cards/${id}/restore`, {}),
  /** Prices by item (design 01c 3.8): all my rooms, or one. */
  prices: (q: { room?: Id; months?: number } = {}) => {
    const p = new URLSearchParams()
    if (q.room !== undefined) p.set('room', String(q.room))
    if (q.months !== undefined) p.set('months', String(q.months))
    const qs = p.toString()
    return get<PriceTraces>(`/api/prices${qs ? `?${qs}` : ''}`)
  },
  /** A family event (no room, design 01c 2.3); notify: "새 일정" to the others. */
  createFamilyEvent: (body: FamilyEventCreate) => post<{ card: Card }>('/api/calendar/events', body),
  card: (id: Id) => get<{ card: Card }>(`/api/cards/${id}`),
  updateCard: (id: Id, body: CardUpdate) => patch<{ card: Card }>(`/api/cards/${id}`, body),
  deleteCard: (id: Id) => del<void>(`/api/cards/${id}`),
  rsvp: (id: Id, answer: 'yes' | 'no' | 'maybe' | 'none') => post<{ card: Card }>(`/api/cards/${id}/rsvp`, { answer }),
  addItem: (id: Id, body: PurchaseItemInput) => post<{ card: Card; item_id: Id }>(`/api/cards/${id}/items`, body),
  /** Several new items at once (the cart's table): all or none. */
  addItems: (id: Id, items: (PurchaseItemInput & { name: string })[]) =>
    post<{ card: Card; item_ids: Id[] }>(`/api/cards/${id}/items/batch`, { items }),
  updateItem: (id: Id, itemId: Id, body: PurchaseItemInput & { version?: number }) =>
    patch<{ card: Card; stale: boolean }>(`/api/cards/${id}/items/${itemId}`, body),
  deleteItem: (id: Id, itemId: Id) => del<{ card: Card }>(`/api/cards/${id}/items/${itemId}`),
  orderItems: (id: Id, ids: Id[]) => post<{ card: Card }>(`/api/cards/${id}/items/order`, { ids }),
  going: (id: Id, going: boolean) => post<{ card: Card }>(`/api/cards/${id}/going`, { going }),
  frequentItems: (roomId: Id) => get<{ items: FrequentItem[] }>(`/api/rooms/${roomId}/frequent-items`),
  spending: (roomId: Id, months: number) =>
    get<{ months: MonthSpending[] }>(`/api/rooms/${roomId}/spending?months=${months}`),
  /** A month of the room's income and spending (tensor tab); month "2026-10". */
  ledger: (roomId: Id, month: string) =>
    get<LedgerMonth>(`/api/rooms/${roomId}/ledger?month=${encodeURIComponent(month)}`),
  addLedger: (roomId: Id, body: LedgerEntryInput & Required<Pick<LedgerEntryInput, 'kind' | 'day' | 'item' | 'amount'>>) =>
    post<{ entry: LedgerEntry }>(`/api/rooms/${roomId}/ledger`, body),
  updateLedger: (id: Id, body: LedgerEntryInput) => patch<{ entry: LedgerEntry }>(`/api/ledger/${id}`, body),
  deleteLedger: (id: Id) => del<void>(`/api/ledger/${id}`),
  /** The link to tensor (design 02 12.3): the workbooks I can see, a room's
      workbook, sending a month to it, and a tensor card's numbers now. */
  tensorLinkWorkbooks: () => get<{ workbooks: TensorLinkWorkbook[] }>('/api/tensor-link/workbooks'),
  roomTensorLink: (roomId: Id) => get<{ link: RoomTensorLink | null }>(`/api/rooms/${roomId}/tensor-link`),
  setRoomTensorLink: (roomId: Id, workbookId: Id) =>
    put<{ link: RoomTensorLink | null }>(`/api/rooms/${roomId}/tensor-link`, { workbook_id: workbookId }),
  clearRoomTensorLink: (roomId: Id) => del<void>(`/api/rooms/${roomId}/tensor-link`),
  sendToTensor: (roomId: Id, month: string) => post<TensorLinkSent>(`/api/rooms/${roomId}/tensor-link/send`, { month }),
  tensorCardValue: (cardId: Id) => get<TensorCardValue>(`/api/cards/${cardId}/tensor`),
  /** A product page's price and title, read by the server (online carts). */
  linkPrice: (url: string) => post<LinkPrice>('/api/links/price', { url }),
  vote: (id: Id, optionIds: Id[]) => put<{ card: Card }>(`/api/cards/${id}/vote`, { option_ids: optionIds }),
  addPollOption: (id: Id, text: string) =>
    post<{ card: Card; option_id: Id }>(`/api/cards/${id}/options`, { text }),
  /** Ends a live location share (the sharer or an admin); the last position stays on the card. */
  stopLive: (cardId: Id) => post<{ card: Card }>(`/api/cards/${cardId}/live/stop`),
  /** My position (or a heartbeat without coordinates) for all my live shares. */
  locReport: (body: LocReport) => post<LocReportAnswer>('/api/loc/report', body),
  /** Where the live shares I can see are now. */
  locLive: () => get<{ live: LiveLocation[] }>('/api/loc/live'),
  places: () => get<{ places: Place[] }>('/api/places'),
  addPlace: (body: PlaceInput & Required<Pick<PlaceInput, 'name' | 'lat_e6' | 'lng_e6'>>) =>
    post<{ place: Place }>('/api/places', body),
  updatePlace: (id: Id, body: PlaceInput) => patch<{ place: Place }>(`/api/places/${id}`, body),
  deletePlace: (id: Id) => del<void>(`/api/places/${id}`),
  trailSettings: () => get<{ trails: TrailSettings }>('/api/me/trails'),
  setTrailSettings: (body: { enabled?: boolean; shared?: boolean; recording?: boolean; interval_s?: TrailInterval }) =>
    put<{ trails: TrailSettings }>('/api/me/trails', body),
  deleteTrails: () => del<void>('/api/me/trails'),
  /** This event (each time it repeats) records my trails, or no longer. */
  trailEvent: (cardId: Id, on: boolean) =>
    on
      ? put<{ trails: TrailSettings }>(`/api/me/trails/events/${cardId}`, {})
      : del<{ trails: TrailSettings }>(`/api/me/trails/events/${cardId}`),
  /** Where someone's trail map opens: the middle of the heaviest edge (404: none). */
  trailsCenter: (userId: Id) => get<{ lat_e6: number; lng_e6: number }>(`/api/trails/${userId}/center`),
  /** My location helper (design 01c 4.4), when one is paired. */
  helper: () => get<{ helper: HelperInfo | null }>('/api/me/helper'),
  unpairHelper: () => del<void>('/api/me/helper'),
  /** A one-time code (10 minutes) the helper exchanges for its session. */
  helperCode: () => post<{ code: string; expires_at: number }>('/api/loc/helper/code'),
  /** Whose trails I may see: me, and who shows theirs to the family. */
  trailUsers: () => get<{ user_ids: Id[] }>('/api/trails'),
  /** One person's trail edges inside a box (micro-degrees). */
  trails: (userId: Id, box: { min_lat_e6: number; min_lng_e6: number; max_lat_e6: number; max_lng_e6: number }) => {
    const q = new URLSearchParams({
      min_lat_e6: String(box.min_lat_e6),
      min_lng_e6: String(box.min_lng_e6),
      max_lat_e6: String(box.max_lat_e6),
      max_lng_e6: String(box.max_lng_e6),
    })
    return get<Trails>(`/api/trails/${userId}?${q}`)
  },
  board: (roomId: Id) => get<{ slots: BoardSlot[]; settings: BoardSettings }>(`/api/rooms/${roomId}/board`),
  boardSettings: (roomId: Id, body: BoardSettingsUpdate) =>
    put<{ settings: BoardSettings }>(`/api/rooms/${roomId}/board/settings`, body),
  createSlot: (roomId: Id, body: BoardSlotCreate) => post<{ slot: BoardSlot }>(`/api/rooms/${roomId}/board`, body),
  orderSlots: (roomId: Id, ids: Id[]) => post<{ slots: BoardSlot[] }>(`/api/rooms/${roomId}/board/order`, { ids }),
  slot: (id: Id) => get<{ slot: BoardSlot }>(`/api/board/slots/${id}`),
  updateSlot: (id: Id, body: BoardSlotUpdate) => patch<{ slot: BoardSlot }>(`/api/board/slots/${id}`, body),
  deleteSlot: (id: Id) => del<void>(`/api/board/slots/${id}`),
  ackSlot: (id: Id, on: boolean) => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/ack`, { on }),
  claimSlot: (id: Id, on: boolean, force = false) =>
    post<{ slot: BoardSlot }>(`/api/board/slots/${id}/claim`, { on, ...(force ? { force } : {}) }),
  markSlot: (id: Id, mark: 'active' | 'done' | 'used') => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/mark`, { mark }),
  takedownSlot: (id: Id) => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/takedown`),
  restoreSlot: (id: Id) => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/restore`),
  addSlotItems: (id: Id, body: BoardSlotItems) => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/items`, body),
  removeSlotFile: (id: Id, fileId: string) =>
    del<{ slot: BoardSlot }>(`/api/board/slots/${id}/files/${encodeURIComponent(fileId)}`),
  removeSlotCard: (id: Id, cardId: Id) => del<{ slot: BoardSlot }>(`/api/board/slots/${id}/cards/${cardId}`),
  addEntry: (id: Id, seq: number) => post<{ slot: BoardSlot }>(`/api/board/slots/${id}/entries`, { seq }),
  removeEntry: (id: Id, seq: number) => del<{ slot: BoardSlot }>(`/api/board/slots/${id}/entries/${seq}`),
  read: (id: Id, seq: number) => post<{ badge: number }>(`/api/rooms/${id}/read`, { seq }),
  mute: (id: Id, muted: boolean) => put<void>(`/api/rooms/${id}/mute`, { muted }),
  pin: (id: Id, pinned: boolean) => put<void>(`/api/rooms/${id}/pin`, { pinned }),
  setNotice: (id: Id, seq: number) => put<void>(`/api/rooms/${id}/notice`, { seq }),
  clearNotice: (id: Id) => del<void>(`/api/rooms/${id}/notice`),

  adminUsers: () => get<{ users: AdminUser[] }>('/api/admin/users'),
  adminHealth: () => get<{ health: AdminHealth }>('/api/admin/health'),
  /** The newest lines of the server log, oldest first (1 to 1000). */
  adminLogs: (lines: number) => get<{ lines: string[] }>(`/api/admin/logs?lines=${lines}`),
  adminBoardRules: () => get<{ stats: AdminBoardRules }>('/api/admin/board-rules'),
  createInvite: (name: string, role: 'member' | 'admin') =>
    post<{ invite: Invite }>('/api/admin/invites', { name, role }),
  invites: () => get<{ invites: InviteListItem[] }>('/api/admin/invites'),
  revokeInvite: (id: Id) => del<void>(`/api/admin/invites/${id}`),
  resetLink: (userId: Id) => post<{ invite: Invite }>(`/api/admin/users/${userId}/reset`),
  disableUser: (userId: Id) => post<void>(`/api/admin/users/${userId}/disable`),
  enableUser: (userId: Id) => post<void>(`/api/admin/users/${userId}/enable`),
  /** A person's signed-in devices, and signing one out (design 7.4, a lost phone). */
  adminUserSessions: (userId: Id) => get<{ sessions: Session[] }>(`/api/admin/users/${userId}/sessions`),
  adminRevokeSession: (sessionId: Id) => del<void>(`/api/admin/sessions/${sessionId}`),
}

/** A short Korean message for an error, for the screen. */
export function errorText(e: unknown): string {
  if (e instanceof NetworkError) return '서버에 연결할 수 없습니다. 잠시 뒤에 다시 해 보세요.'
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'invalid_credentials':
        return '아이디나 비밀번호가 맞지 않습니다.'
      case 'too_many_attempts': {
        const min = Math.max(1, Math.ceil((e.retryAfterMs ?? 60000) / 60000))
        return `잘못 입력한 횟수가 많습니다. ${min}분 뒤에 다시 해 보세요.`
      }
      case 'unauthorized':
        return '다시 로그인해 주세요.'
      case 'forbidden':
        return '권한이 없습니다.'
      case 'bad_origin':
        return '다른 사이트에서 온 요청이라 거부되었습니다.'
      case 'not_found':
        return '찾을 수 없습니다. 링크가 만료되었거나 이미 사용되었을 수 있습니다.'
      case 'conflict':
        // tensor: "a view has that name" (sheets, dimensions, tensors, names too)
        return e.message.startsWith('name:') || e.message.endsWith('has that name')
          ? '같은 이름이 이미 있습니다.'
          : '이미 사용 중인 아이디입니다.'
      case 'payload_too_large':
        return '내용이 너무 깁니다.'
      case 'storage_unavailable':
        return '파일 저장소를 지금 쓸 수 없습니다. 잠시 뒤에 다시 해 보거나 관리자에게 알려 주세요.'
      case 'no_space':
        return '서버의 저장 공간이 부족합니다. 관리자에게 알려 주세요.'
      case 'board_full':
        return '칠판이 가득 찼습니다. 다 쓴 것을 내린 뒤에 다시 해 보세요.'
      case 'duplicate':
        return '이미 칠판에 있는 기프티콘입니다.'
      case 'claimed':
        return '다른 가족이 쓸 예정인 기프티콘입니다.'
      default:
        return e.message || '오류가 발생했습니다.'
    }
  }
  if (e instanceof UserError) return e.message
  return '오류가 발생했습니다.'
}
